import React, { useState, useMemo } from 'react';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { useSectionControl } from '../contexts/SectionControlContext';
import { useTranslations } from '../contexts/TranslationContext';
import {
  SECTION_WIDTHS_LEGACY,
  SECTION_HEIGHTS,
  WIDTH_FALLBACK_LABELS,
  HEIGHT_FALLBACK_LABELS,
  getSectionWidth,
  getSectionHeight,
  getSectionStretch,
  getColumnSpanOptions,
  getWidthLabelKey,
  getHeightLabelKey,
  normalizeWidth,
  SPACER_HEIGHTS,
  SPACER_HEIGHT_LABELS,
  getSpacerHeight,
  isSpacerSection,
  isContentBlockSection,
  CONTENT_BLOCK_COLORS,
  CONTENT_BLOCK_GRADIENTS,
  getContentBlockBgType,
  getContentBlockBgColor,
  getContentBlockBgGradient,
  getContentBlockHeight,
  CONTENT_BLOCK_HEIGHTS,
  CONTENT_BLOCK_HEIGHT_LABELS,
} from '../utils/sectionWidth';

/**
 * Inline width / height / stretch controls shown in the top-right corner
 * of a section card when the user is an admin. Purple (width), green
 * (height), cyan (stretch toggle) for visual separation.
 *
 * Width buttons adapt to the grid context:
 * - Grid mode (gridColumns available): Full | 1 | 2 | 3 | ... (numeric column spans)
 * - Stacked mode (no gridColumns): Full | 2/3 | 1/2 | 1/3 (legacy)
 *
 * For spacer sections: shows purple width + orange spacer-height buttons
 * instead of green row-height + cyan stretch.
 *
 * For content block sections: shows standard controls + color/gradient swatches
 * + text align/size controls.
 */
export default function EditModeSectionConfig({ sectionType, editMode }) {
  const { isAdmin } = useIsAdmin();
  const sectionControl = useSectionControl();
  const { t } = useTranslations();
  const [busy, setBusy] = useState(false);

  const gridColumns = sectionControl?.gridColumns;

  // Build width options based on grid context
  // Hide width picker entirely when gridColumns <= 1 (both 'full' and '1' are the same)
  const widthOptions = useMemo(() => {
    if (gridColumns && gridColumns <= 1) return [];
    if (gridColumns) return getColumnSpanOptions(gridColumns);
    return SECTION_WIDTHS_LEGACY;
  }, [gridColumns]);

  if (!editMode && !isAdmin) return null;

  const section = sectionControl?.getSectionByType?.(sectionType);
  const hasControls = isAdmin && section && sectionControl?.updateConfig;

  if (!hasControls) return null;

  const isSpacer = isSpacerSection(sectionType);
  const isContentBlock = isContentBlockSection(sectionType);
  const rawWidth = getSectionWidth(section);
  // Normalize legacy widths to numeric when in grid mode
  const currentWidth = gridColumns ? normalizeWidth(rawWidth, gridColumns) : rawWidth;

  const handleWidth = async (e, width) => {
    e.stopPropagation();
    if (busy || width === currentWidth) return;
    setBusy(true);
    try { await sectionControl.updateConfig(section.id, { width }); } finally { setBusy(false); }
  };

  // Spacer sections: orange height picker
  if (isSpacer) {
    const currentSpacerHeight = getSpacerHeight(section);
    const handleSpacerHeight = async (e, spacerHeight) => {
      e.stopPropagation();
      if (busy || spacerHeight === currentSpacerHeight) return;
      setBusy(true);
      try { await sectionControl.updateConfig(section.id, { spacerHeight }); } finally { setBusy(false); }
    };

    return (
      <div
        className={`absolute top-0 right-0 z-50 bg-purple-600 text-[11px] font-bold rounded-bl-lg shadow-md select-none tracking-wide flex items-center ${busy ? 'opacity-60' : ''}`}
        style={{ color: '#ffffff' }}
      >
        {/* Width buttons — hidden when no meaningful options */}
        {widthOptions.length > 0 && (
          <div className="flex items-center border-r border-purple-400/40">
            {widthOptions.map((w) => (
              <button
                key={w}
                onClick={(e) => handleWidth(e, w)}
                className={`px-1.5 py-1 transition-colors text-[10px] ${
                  w === currentWidth
                    ? 'bg-white/25 font-extrabold'
                    : 'hover:bg-purple-500'
                }`}
                title={`${t('admin.sections.widthLabel')}: ${t(getWidthLabelKey(w))}`}
              >
                {t(getWidthLabelKey(w))}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center bg-orange-500">
          {SPACER_HEIGHTS.map((h) => (
            <button
              key={h}
              onClick={(e) => handleSpacerHeight(e, h)}
              className={`px-1.5 py-1 transition-colors text-[10px] ${
                h === currentSpacerHeight
                  ? 'bg-white/25 font-extrabold'
                  : 'hover:bg-orange-400'
              }`}
              title={`${t('admin.sections.spacerHeightLabel')}: ${SPACER_HEIGHT_LABELS[h]}`}
            >
              {SPACER_HEIGHT_LABELS[h]}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Non-spacer sections: regular green height + cyan stretch
  const currentHeight = getSectionHeight(section);
  const currentStretch = getSectionStretch(section);

  const handleHeight = async (e, height) => {
    e.stopPropagation();
    if (busy || height === currentHeight) return;
    setBusy(true);
    try { await sectionControl.updateConfig(section.id, { height }); } finally { setBusy(false); }
  };

  const handleStretch = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try { await sectionControl.updateConfig(section.id, { stretch: !currentStretch }); } finally { setBusy(false); }
  };

  // Content block: standard controls + color/gradient row + text controls
  if (isContentBlock) {
    const bgType = getContentBlockBgType(section);
    const currentBgColor = getContentBlockBgColor(section);
    const currentBgGradient = getContentBlockBgGradient(section);

    const handleBgType = async (e, newType) => {
      e.stopPropagation();
      if (busy || newType === bgType) return;
      setBusy(true);
      try {
        const patch = { bgType: newType };
        // Auto-set text color based on active preset
        if (newType === 'gradient') {
          const preset = CONTENT_BLOCK_GRADIENTS.find(g => g.gradient === currentBgGradient);
          if (preset) patch.textColor = preset.textColor;
        } else {
          const preset = CONTENT_BLOCK_COLORS.find(c => c.hex === currentBgColor);
          if (preset) patch.textColor = preset.textColor;
        }
        await sectionControl.updateConfig(section.id, patch);
      } finally { setBusy(false); }
    };

    const handleSolidColor = async (e, color) => {
      e.stopPropagation();
      if (busy) return;
      setBusy(true);
      try {
        await sectionControl.updateConfig(section.id, {
          bgType: 'solid', bgColor: color.hex, textColor: color.textColor
        });
      } finally { setBusy(false); }
    };

    const handleGradient = async (e, grad) => {
      e.stopPropagation();
      if (busy) return;
      setBusy(true);
      try {
        await sectionControl.updateConfig(section.id, {
          bgType: 'gradient', bgGradient: grad.gradient, textColor: grad.textColor
        });
      } finally { setBusy(false); }
    };

    const currentBlockHeight = getContentBlockHeight(section);
    const handleBlockHeight = async (e, bh) => {
      e.stopPropagation();
      if (busy || bh === currentBlockHeight) return;
      setBusy(true);
      try { await sectionControl.updateConfig(section.id, { blockHeight: bh }); } finally { setBusy(false); }
    };

    const swatches = bgType === 'gradient' ? CONTENT_BLOCK_GRADIENTS : CONTENT_BLOCK_COLORS;
    const activeValue = bgType === 'gradient' ? currentBgGradient : currentBgColor;

    return (
      <>
        {/* Top-right: Width + Height + Stretch + Text controls */}
        <div className={`absolute top-0 right-0 z-50 text-[11px] font-bold shadow-md select-none tracking-wide flex flex-col items-end ${busy ? 'opacity-60' : ''}`}>
          {/* Row 1: Width + Height + Stretch */}
          <div className="flex items-center bg-purple-600 rounded-bl-lg" style={{ color: '#ffffff' }}>
            {widthOptions.length > 0 && (
              <div className="flex items-center border-r border-purple-400/40">
                {widthOptions.map((w) => (
                  <button
                    key={w}
                    onClick={(e) => handleWidth(e, w)}
                    className={`px-1.5 py-1 transition-colors text-[10px] ${
                      w === currentWidth ? 'bg-white/25 font-extrabold' : 'hover:bg-purple-500'
                    }`}
                    title={`${t('admin.sections.widthLabel')}: ${t(getWidthLabelKey(w))}`}
                  >
                    {t(getWidthLabelKey(w))}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center bg-emerald-600">
              {CONTENT_BLOCK_HEIGHTS.map((bh) => (
                <button
                  key={bh}
                  onClick={(e) => handleBlockHeight(e, bh)}
                  className={`px-1.5 py-1 transition-colors text-[10px] ${
                    bh === currentBlockHeight ? 'bg-white/25 font-extrabold' : 'hover:bg-emerald-500'
                  }`}
                  title={`${t('admin.sections.heightLabel')}: ${CONTENT_BLOCK_HEIGHT_LABELS[bh]}`}
                >
                  {CONTENT_BLOCK_HEIGHT_LABELS[bh]}
                </button>
              ))}
            </div>
            <button
              onClick={handleStretch}
              className={`px-1.5 py-1 transition-colors text-[10px] ${
                currentStretch ? 'bg-cyan-600 hover:bg-cyan-500 font-extrabold' : 'bg-gray-500 hover:bg-gray-400'
              }`}
              title={`${currentStretch ? `${t('admin.sections.stretchOn')}` : `${t('admin.sections.stretchOff')}`}`}
            >
              ↕
            </button>
          </div>


        </div>

        {/* Bottom-right: Solid/Gradient toggle + color swatches */}
        <div className={`absolute bottom-0 right-0 z-50 text-[11px] font-bold shadow-md select-none tracking-wide ${busy ? 'opacity-60' : ''}`}>
          <div className="flex items-center bg-gray-800 rounded-tl-lg" style={{ color: '#ffffff' }}>
            <button
              onClick={(e) => handleBgType(e, 'solid')}
              className={`px-1.5 py-1 transition-colors text-[10px] ${
                bgType === 'solid' ? 'bg-amber-500 font-extrabold' : 'bg-gray-700 hover:bg-gray-600'
              }`}
              title={t('admin.sections.solidColor')}
            >
              ■
            </button>
            <button
              onClick={(e) => handleBgType(e, 'gradient')}
              className={`px-1.5 py-1 transition-colors text-[10px] border-r border-gray-600 ${
                bgType === 'gradient' ? 'bg-amber-500 font-extrabold' : 'bg-gray-700 hover:bg-gray-600'
              }`}
              title={t('admin.sections.gradient')}
            >
              ◆
            </button>
            <div className="flex items-center gap-px px-1">
              {swatches.map((swatch, i) => {
                const swatchValue = bgType === 'gradient' ? swatch.gradient : swatch.hex;
                const isActive = swatchValue === activeValue;
                const swatchStyle = bgType === 'gradient'
                  ? { background: swatch.gradient }
                  : { backgroundColor: swatch.hex };
                return (
                  <button
                    key={i}
                    onClick={(e) => bgType === 'gradient' ? handleGradient(e, swatch) : handleSolidColor(e, swatch)}
                    className={`w-4 h-4 rounded-full border transition-all ${
                      isActive ? 'border-white scale-125 ring-1 ring-white' : 'border-gray-500 hover:scale-110'
                    }`}
                    style={swatchStyle}
                    title={swatch.name}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div
      className={`absolute top-0 right-0 z-50 bg-purple-600 text-[11px] font-bold rounded-bl-lg shadow-md select-none tracking-wide flex items-center ${busy ? 'opacity-60' : ''}`}
      style={{ color: '#ffffff' }}
    >
      {/* Width buttons — hidden when no meaningful options */}
      {widthOptions.length > 0 && (
        <div className="flex items-center border-r border-purple-400/40">
          {widthOptions.map((w) => (
            <button
              key={w}
              onClick={(e) => handleWidth(e, w)}
              className={`px-1.5 py-1 transition-colors text-[10px] ${
                w === currentWidth
                  ? 'bg-white/25 font-extrabold'
                  : 'hover:bg-purple-500'
              }`}
              title={`${t('admin.sections.widthLabel')}: ${t(getWidthLabelKey(w))}`}
            >
              {t(getWidthLabelKey(w))}
            </button>
          ))}
        </div>
      )}

      {/* Height buttons — green for visual separation */}
      <div className="flex items-center bg-emerald-600">
        {SECTION_HEIGHTS.map((h) => (
          <button
            key={h}
            onClick={(e) => handleHeight(e, h)}
            className={`px-1.5 py-1 transition-colors text-[10px] ${
              h === currentHeight
                ? 'bg-white/25 font-extrabold'
                : 'hover:bg-emerald-500'
            }`}
            title={`${t('admin.sections.heightLabel')}: ${t(getHeightLabelKey(h))}`}
          >
            {h === 1 ? '1r' : '2r'}
          </button>
        ))}
      </div>

      {/* Stretch toggle — cyan for visual separation */}
      <button
        onClick={handleStretch}
        className={`px-1.5 py-1 transition-colors text-[10px] rounded-bl-lg ${
          currentStretch
            ? 'bg-cyan-600 hover:bg-cyan-500 font-extrabold'
            : 'bg-gray-500 hover:bg-gray-400'
        }`}
        title={currentStretch ? t('admin.sections.stretchOnDesc') : t('admin.sections.stretchOffDesc')}
      >
        ↕
      </button>
    </div>
  );
}
